<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :modal="true"
    :maximizable="true"
    :dismissableMask="true"
    :style="{ width: '92vw', height: '92vh' }"
    :contentStyle="{ padding: '0', height: '100%', overflow: 'hidden' }"
    class="user-overview-dialog"
  >
    <template #header>
      <div class="flex items-center gap-3 mr-8">
        <i class="pi pi-user text-xl"></i>
        <div>
          <span class="text-lg font-bold">{{ user?.name || 'User Overview' }}</span>
          <span v-if="user?.email" class="text-sm text-gray-500 ml-2">{{ user.email }}</span>
        </div>
        <Tag v-if="user?.userStatus" :value="user.userStatus"
          :severity="user.userStatus === 'active' ? 'success' : user.userStatus === 'deactivated' ? 'danger' : 'warn'" />
      </div>
    </template>

    <div class="flex flex-col h-full">
      <Tabs :value="activeTab" @update:value="onTabChange" class="flex flex-col flex-1 min-h-0">
        <TabList>
          <Tab value="profile"><i class="pi pi-id-card mr-1"></i> Profile</Tab>
          <Tab value="devtips"><i class="pi pi-lightbulb mr-1"></i> DevTips</Tab>
          <Tab value="travel-buddy"><i class="pi pi-users mr-1"></i> Travel Buddy</Tab>
          <Tab value="assessments"><i class="pi pi-chart-bar mr-1"></i> Assessments</Tab>
          <Tab value="levels"><i class="pi pi-star mr-1"></i> Levels</Tab>
          <Tab value="kss"><i class="pi pi-comments mr-1"></i> KSS</Tab>
          <Tab value="personal-goals"><i class="pi pi-bullseye mr-1"></i> Personal Goals</Tab>
          <Tab value="audit"><i class="pi pi-history mr-1"></i> Audit</Tab>
        </TabList>
        <TabPanels class="flex-1 min-h-0 overflow-y-auto">

          <!-- PROFILE TAB -->
          <TabPanel value="profile">
            <div v-if="loading.profile" class="flex justify-center p-6"><ProgressSpinner /></div>
            <div v-else-if="data.profile" class="p-4">
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <!-- Identity Card -->
                <div class="surface-card border-round p-4 shadow-1">
                  <h3 class="text-lg font-semibold mb-3 flex items-center gap-2">
                    <i class="pi pi-user"></i> Identity
                  </h3>
                  <div class="flex flex-col gap-2">
                    <CopyableField label="Name" :value="data.profile.identity.name" />
                    <CopyableField label="Email" :value="data.profile.identity.email" />
                    <CopyableField label="Phone" :value="data.profile.identity.mobileNumber || ''">
                      {{ data.profile.identity.mobileNumber || '—' }}
                    </CopyableField>
                    <div class="flex justify-between"><span class="text-gray-500">Status</span>
                      <Tag :value="data.profile.identity.userStatus" :severity="data.profile.identity.userStatus === 'active' ? 'success' : 'danger'" />
                    </div>
                    <div class="flex justify-between"><span class="text-gray-500">Valid</span><span>{{ data.profile.identity.isValid ? 'Yes' : 'No' }}</span></div>
                    <div class="flex justify-between"><span class="text-gray-500">Level</span>
                      <Tag :value="'Level ' + data.profile.currentLevel" severity="info" />
                    </div>
                    <div class="flex justify-between items-start"><span class="text-gray-500">Roles</span>
                      <div class="flex flex-wrap gap-1 justify-end">
                        <Tag v-for="role in (data.profile.identity.roles || [])" :key="role" :value="role" severity="secondary" />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Settings Card -->
                <div class="surface-card border-round p-4 shadow-1">
                  <h3 class="text-lg font-semibold mb-3 flex items-center gap-2">
                    <i class="pi pi-cog"></i> Settings
                  </h3>
                  <div class="flex flex-col gap-2">
                    <div class="flex justify-between"><span class="text-gray-500">Language</span><span class="font-medium">{{ (data.profile.settings.preferredLanguage || 'en').toUpperCase() }}</span></div>
                    <div class="flex justify-between"><span class="text-gray-500">Timezone</span><span class="font-medium">{{ data.profile.settings.timezone || '—' }}</span></div>
                    <div class="flex justify-between"><span class="text-gray-500">Email notifications</span><span>{{ data.profile.settings.mailAllowed ? 'Yes' : 'No' }}</span></div>
                    <div class="flex justify-between"><span class="text-gray-500">Push notifications</span><span>{{ data.profile.settings.pushAllowed ? 'Yes' : 'No' }}</span></div>
                    <div class="flex justify-between"><span class="text-gray-500">SMS allowed</span><span>{{ data.profile.settings.SMSallowed ? 'Yes' : 'No' }}</span></div>
                    <div class="flex justify-between"><span class="text-gray-500">Working day</span><span>{{ data.profile.settings.workingDayStart || '—' }} – {{ data.profile.settings.workingDayEnd || '—' }}</span></div>
                  </div>
                </div>

                <!-- Feature Flags Card -->
                <div class="surface-card border-round p-4 shadow-1">
                  <h3 class="text-lg font-semibold mb-3 flex items-center gap-2">
                    <i class="pi pi-sliders-h"></i> Feature Flags
                  </h3>
                  <div class="flex flex-col gap-2">
                    <div class="flex justify-between"><span class="text-gray-500">Travel Buddy</span>
                      <Tag :value="data.profile.features.travelBuddyActivated ? 'Active' : 'Inactive'" :severity="data.profile.features.travelBuddyActivated ? 'success' : 'secondary'" />
                    </div>
                    <div class="flex justify-between"><span class="text-gray-500">On Holiday</span><span>{{ data.profile.features.onHoliday ? 'Yes' : 'No' }}</span></div>
                    <div class="flex justify-between"><span class="text-gray-500">Reflection Guidelines</span>
                      <Tag :value="data.profile.features.reflexionGuidelineActivated ? 'Active' : 'Inactive'" :severity="data.profile.features.reflexionGuidelineActivated ? 'success' : 'secondary'" />
                    </div>
                    <div class="flex justify-between"><span class="text-gray-500">External Evaluation</span>
                      <Tag :value="data.profile.features.externalEvaluationEnabled ? 'Enabled' : 'Disabled'" :severity="data.profile.features.externalEvaluationEnabled ? 'success' : 'secondary'" />
                    </div>
                  </div>
                </div>

                <!-- Dates Card -->
                <div class="surface-card border-round p-4 shadow-1">
                  <h3 class="text-lg font-semibold mb-3 flex items-center gap-2">
                    <i class="pi pi-calendar"></i> Activity
                  </h3>
                  <div class="flex flex-col gap-2">
                    <div class="flex justify-between"><span class="text-gray-500">Registered</span><span class="font-medium">{{ formatDate(data.profile.identity.date_registered) }}</span></div>
                    <div class="flex justify-between"><span class="text-gray-500">Last Login</span><span class="font-medium">{{ formatDate(data.profile.identity.date_lastLogin) }}</span></div>
                    <div class="flex justify-between"><span class="text-gray-500">Last Activity</span><span class="font-medium">{{ formatDate(data.profile.identity.date_lastActivity) }}</span></div>
                  </div>
                </div>
              </div>

              <!-- Tags & Teams -->
              <div v-if="(data.profile.tags && data.profile.tags.length) || (data.profile.teams && data.profile.teams.length)" class="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div v-if="data.profile.tags && data.profile.tags.length" class="surface-card border-round p-4 shadow-1">
                  <h3 class="text-lg font-semibold mb-3"><i class="pi pi-tag mr-1"></i> Tags</h3>
                  <div class="flex flex-wrap gap-1 items-center">
                    <template v-for="(tag, i) in data.profile.tags" :key="i">
                      <Tag
                        v-if="typeof tag === 'object' && tag"
                        :value="getLocalizedText(tag.tag) || String(tag._id || '—')"
                        severity="info"
                        :title="getLocalizedText(tag.identifier) || ''"
                      />
                      <Tag
                        v-else
                        :value="String(tag)"
                        severity="info"
                      />
                    </template>
                  </div>
                </div>
                <div v-if="data.profile.teams && data.profile.teams.length" class="surface-card border-round p-4 shadow-1">
                  <h3 class="text-lg font-semibold mb-3"><i class="pi pi-sitemap mr-1"></i> Teams</h3>
                  <div class="flex flex-wrap gap-1">
                    <Tag
                      v-for="(team, i) in data.profile.teams"
                      :key="i"
                      :value="typeof team === 'string' ? team : (team?.name || team?.value || String(team?._id || '—'))"
                      severity="warn"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="p-6 text-center text-gray-400">No profile data available.</div>
          </TabPanel>

          <!-- DEVTIPS TAB -->
          <TabPanel value="devtips">
            <div v-if="loading.devtips" class="flex justify-center p-6"><ProgressSpinner /></div>
            <div v-else-if="data.devtips" class="p-4">
              <div class="mb-3 text-sm text-gray-500">{{ data.devtips.total }} DevTips total</div>
              <DataTable :value="data.devtips.devTips" :paginator="true" :rows="15" :rowsPerPageOptions="[10, 15, 30, 50]"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                :sortField="'dateExposed'" :sortOrder="-1">
                <Column field="title" header="Title" :sortable="true" style="min-width: 200px">
                  <template #body="{ data: row }">
                    <span class="font-medium">{{ getLocalizedText(row.title) }}</span>
                  </template>
                </Column>
                <Column field="dimension" header="Dimension" :sortable="true">
                  <template #body="{ data: row }">
                    {{ getLocalizedText(row.dimension) }}
                  </template>
                </Column>
                <Column header="Status" :sortable="true" field="isDone" style="width: 100px">
                  <template #body="{ data: row }">
                    <Tag :value="row.isDone ? 'Done' : row.isNew ? 'New' : 'Seen'" :severity="row.isDone ? 'success' : row.isNew ? 'info' : 'secondary'" />
                  </template>
                </Column>
                <Column field="dateExposed" header="Date Exposed" :sortable="true" style="width: 160px">
                  <template #body="{ data: row }">
                    {{ formatDate(row.dateExposed) }}
                  </template>
                </Column>
                <Column field="feedbackGiven" header="Feedback" style="width: 100px">
                  <template #body="{ data: row }">
                    <Tag :value="row.feedbackGiven ? 'Yes' : 'No'" :severity="row.feedbackGiven ? 'success' : 'secondary'" />
                  </template>
                </Column>
                <Column field="hasLinkInContent" header="Has Link" style="width: 90px">
                  <template #body="{ data: row }">
                    {{ row.hasLinkInContent ? 'Yes' : 'No' }}
                  </template>
                </Column>
              </DataTable>
            </div>
            <div v-else class="p-6 text-center text-gray-400">No DevTips data available.</div>
          </TabPanel>

          <!-- TRAVEL BUDDY TAB -->
          <TabPanel value="travel-buddy">
            <div v-if="loading['travel-buddy']" class="flex justify-center p-6"><ProgressSpinner /></div>
            <div v-else-if="data['travel-buddy']" class="p-4">
              <!-- Status Cards -->
              <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Activated</div>
                  <Tag :value="data['travel-buddy'].user?.travelBuddyActivated ? 'Yes' : 'No'" :severity="data['travel-buddy'].user?.travelBuddyActivated ? 'success' : 'secondary'" />
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">On Holiday</div>
                  <Tag :value="data['travel-buddy'].user?.onHoliday ? 'Yes' : 'No'" :severity="data['travel-buddy'].user?.onHoliday ? 'warn' : 'secondary'" />
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Total Meetings</div>
                  <span class="text-xl font-bold">{{ data['travel-buddy'].summary?.totalMeetings ?? 0 }}</span>
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Completed</div>
                  <span class="text-xl font-bold">{{ data['travel-buddy'].summary?.completedMeetings ?? 0 }}</span>
                </div>
              </div>

              <!-- Meetings Table -->
              <h3 class="text-base font-semibold mb-2">Meetings History</h3>
              <DataTable :value="data['travel-buddy'].meetings || []" :paginator="true" :rows="10"
                :rowsPerPageOptions="[10, 20, 50]"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}">
                <Column field="meetingDate" header="Date" :sortable="true" style="width: 140px">
                  <template #body="{ data: row }">
                    {{ formatDate(row.meetingDate) }}
                  </template>
                </Column>
                <Column field="meetingStatus" header="Status" :sortable="true" style="width: 120px">
                  <template #body="{ data: row }">
                    <Tag :value="row.meetingStatus" :severity="meetingStatusSeverity(row.meetingStatus)" />
                  </template>
                </Column>
                <Column field="buddyName" header="Buddy" style="min-width: 150px">
                  <template #body="{ data: row }">
                    {{ row.buddyName || row.buddy?.name || '—' }}
                  </template>
                </Column>
                <Column header="Feedback Given" style="width: 130px">
                  <template #body="{ data: row }">
                    <Tag :value="row.hasFeedback ? 'Yes' : 'No'" :severity="row.hasFeedback ? 'success' : 'secondary'" />
                  </template>
                </Column>
                <Column field="calendarWeek" header="CW" style="width: 60px" />
              </DataTable>
            </div>
            <div v-else class="p-6 text-center text-gray-400">No Travel Buddy data available.</div>
          </TabPanel>

          <!-- ASSESSMENTS TAB -->
          <TabPanel value="assessments">
            <div v-if="loading.assessments" class="flex justify-center p-6"><ProgressSpinner /></div>
            <div v-else-if="data.assessments" class="p-4">
              <!-- Summary Cards -->
              <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Total</div>
                  <span class="text-xl font-bold">{{ data.assessments.total }}</span>
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Completed</div>
                  <span class="text-xl font-bold text-green-600">{{ assessmentCounts.completed }}</span>
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Open</div>
                  <span class="text-xl font-bold text-blue-600">{{ assessmentCounts.open }}</span>
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Types</div>
                  <span class="text-xl font-bold">{{ assessmentCounts.types }}</span>
                </div>
              </div>

              <DataTable :value="data.assessments.assessments" :paginator="true" :rows="15"
                :rowsPerPageOptions="[10, 15, 30, 50]"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                :sortField="'date_created'" :sortOrder="-1">
                <Column field="type" header="Type" :sortable="true" style="min-width: 140px">
                  <template #body="{ data: row }">
                    <Tag :value="row.type" severity="info" />
                  </template>
                </Column>
                <Column field="title" header="Title" :sortable="true" style="min-width: 180px">
                  <template #body="{ data: row }">
                    {{ getLocalizedText(row.title) || row.type }}
                  </template>
                </Column>
                <Column field="status" header="Status" :sortable="true" style="width: 120px">
                  <template #body="{ data: row }">
                    <Tag :value="row.status" :severity="assessmentStatusSeverity(row.status)" />
                  </template>
                </Column>
                <Column field="date_created" header="Created" :sortable="true" style="width: 140px">
                  <template #body="{ data: row }">
                    {{ formatDate(row.date_created) }}
                  </template>
                </Column>
                <Column field="date_completed" header="Completed" :sortable="true" style="width: 140px">
                  <template #body="{ data: row }">
                    {{ row.date_completed ? formatDate(row.date_completed) : '—' }}
                  </template>
                </Column>
              </DataTable>
            </div>
            <div v-else class="p-6 text-center text-gray-400">No assessments data available.</div>
          </TabPanel>

          <!-- LEVELS TAB -->
          <TabPanel value="levels">
            <div v-if="loading.levels" class="flex justify-center p-6"><ProgressSpinner /></div>
            <div v-else-if="data.levels" class="p-4">
              <!-- Summary -->
              <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Current Level</div>
                  <span class="text-2xl font-bold">{{ data.levels.currentLevel ?? 0 }}</span>
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Total XP</div>
                  <span class="text-2xl font-bold">{{ levelSummary.totalXP }}</span>
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Levels Completed</div>
                  <span class="text-2xl font-bold">{{ levelSummary.completed }}</span>
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Total Levels</div>
                  <span class="text-2xl font-bold">{{ levelSummary.total }}</span>
                </div>
              </div>

              <!-- Level Details -->
              <h3 class="text-base font-semibold mb-2">Level Progress</h3>
              <DataTable :value="data.levels.levels || []" :paginator="(data.levels.levels || []).length > 10" :rows="10">
                <Column field="level" header="Level" :sortable="true" style="width: 80px">
                  <template #body="{ data: row }">
                    <span class="font-bold">{{ row.level }}</span>
                  </template>
                </Column>
                <Column field="title" header="Title" :sortable="true" style="min-width: 150px" />
                <Column header="Progress" style="min-width: 200px">
                  <template #body="{ data: row }">
                    <div class="flex items-center gap-2">
                      <ProgressBar :value="row.totalXP > 0 ? Math.round((row.currentXP / row.totalXP) * 100) : 0"
                        :showValue="true" style="height: 20px; flex: 1" />
                      <span class="text-xs text-gray-500 whitespace-nowrap">{{ row.currentXP }}/{{ row.totalXP }} XP</span>
                    </div>
                  </template>
                </Column>
                <Column field="completed" header="Completed" :sortable="true" style="width: 110px">
                  <template #body="{ data: row }">
                    <Tag :value="row.completed ? 'Yes' : 'No'" :severity="row.completed ? 'success' : 'secondary'" />
                  </template>
                </Column>
                <Column header="Goals" style="width: 100px">
                  <template #body="{ data: row }">
                    {{ (row.goals || []).filter(g => g.completed).length }}/{{ (row.goals || []).length }}
                  </template>
                </Column>
              </DataTable>
            </div>
            <div v-else class="p-6 text-center text-gray-400">No level data available.</div>
          </TabPanel>

          <!-- KSS TAB -->
          <TabPanel value="kss">
            <div v-if="loading.kss" class="flex justify-center p-6"><ProgressSpinner /></div>
            <div v-else-if="data.kss" class="p-4">
              <!-- Stats -->
              <div class="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">KSS Chosen</div>
                  <span class="text-xl font-bold">{{ data.kss.stats?.kssChoosen ?? 0 }}</span>
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Trackers Created</div>
                  <span class="text-xl font-bold">{{ data.kss.stats?.kssTrackerCreated ?? 0 }}</span>
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-sm text-gray-500 mb-1">Trackers Submitted</div>
                  <span class="text-xl font-bold">{{ data.kss.stats?.kssTrackerSubmitted ?? 0 }}</span>
                </div>
              </div>

              <!-- Created KSS -->
              <h3 class="text-base font-semibold mb-2">Created ({{ (data.kss.created || []).length }})</h3>
              <DataTable v-if="(data.kss.created || []).length > 0" :value="data.kss.created" :paginator="data.kss.created.length > 10" :rows="10" class="mb-4">
                <Column field="name" header="Title" :sortable="true" style="min-width: 180px">
                  <template #body="{ data: row }">
                    {{ getLocalizedText(row.name) || getLocalizedText(row.title) || row.type || '—' }}
                  </template>
                </Column>
                <Column field="status" header="Status" :sortable="true" style="width: 120px">
                  <template #body="{ data: row }">
                    <Tag :value="row.status || '—'" :severity="assessmentStatusSeverity(row.status)" />
                  </template>
                </Column>
                <Column header="Rhythm" style="min-width: 140px">
                  <template #body="{ data: row }">
                    <span class="text-sm">{{ kssRhythmLabel(row) }}</span>
                  </template>
                </Column>
                <Column header="Series end" style="min-width: 110px">
                  <template #body="{ data: row }">
                    <span class="text-sm">{{ kssSeriesEndLabel(row) }}</span>
                  </template>
                </Column>
                <Column header="Planned" style="width: 80px">
                  <template #body="{ data: row }">
                    {{ kssPlannedCount(row) }}
                  </template>
                </Column>
                <Column header="Next 6" style="min-width: 150px">
                  <template #body="{ data: row }">
                    <span class="text-xs text-gray-600">{{ kssUpcomingDates(row).join(', ') || '—' }}</span>
                  </template>
                </Column>
                <Column field="date_created" header="Created" :sortable="true" style="width: 140px">
                  <template #body="{ data: row }">
                    {{ formatDate(row.date_created) }}
                  </template>
                </Column>
                <Column header="Actions" style="width: 160px">
                  <template #body="{ data: row }">
                    <Button
                      v-if="canAdminEndKssSeries(row)"
                      label="End series"
                      icon="pi pi-stop-circle"
                      severity="warn"
                      size="small"
                      outlined
                      :loading="endingKssSeriesId === (row.seriesId || row._id)"
                      @click="confirmEndKssSeries(row)"
                    />
                  </template>
                </Column>
              </DataTable>
              <div v-else class="text-sm text-gray-400 mb-4">No KSS sessions created.</div>

              <!-- Participating KSS -->
              <h3 class="text-base font-semibold mb-2">Participating ({{ (data.kss.participating || []).length }})</h3>
              <DataTable v-if="(data.kss.participating || []).length > 0" :value="data.kss.participating" :paginator="data.kss.participating.length > 10" :rows="10">
                <Column field="title" header="Title" :sortable="true" style="min-width: 180px">
                  <template #body="{ data: row }">
                    {{ getLocalizedText(row.title) || row.type || '—' }}
                  </template>
                </Column>
                <Column field="status" header="Status" :sortable="true" style="width: 120px">
                  <template #body="{ data: row }">
                    <Tag :value="row.status || '—'" :severity="assessmentStatusSeverity(row.status)" />
                  </template>
                </Column>
                <Column field="date_created" header="Created" :sortable="true" style="width: 140px">
                  <template #body="{ data: row }">
                    {{ formatDate(row.date_created) }}
                  </template>
                </Column>
              </DataTable>
              <div v-else class="text-sm text-gray-400">No KSS sessions as participant.</div>
            </div>
            <div v-else class="p-6 text-center text-gray-400">No KSS data available.</div>
          </TabPanel>

          <!-- PERSONAL GOALS TAB -->
          <TabPanel value="personal-goals">
            <div v-if="loading['personal-goals']" class="flex justify-center p-6"><ProgressSpinner /></div>
            <div v-else-if="data['personal-goals']" class="p-4">
              <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-xs text-gray-500">Total goals</div>
                  <div class="text-2xl font-bold">{{ data['personal-goals'].summary?.total ?? 0 }}</div>
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-xs text-gray-500">Active</div>
                  <div class="text-2xl font-bold text-green-700">{{ data['personal-goals'].summary?.active ?? 0 }}</div>
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-xs text-gray-500">Completed</div>
                  <div class="text-2xl font-bold text-blue-700">{{ data['personal-goals'].summary?.completed ?? 0 }}</div>
                </div>
                <div class="surface-card border-round p-3 shadow-1 text-center">
                  <div class="text-xs text-gray-500">Deleted</div>
                  <div class="text-2xl font-bold text-gray-600">{{ data['personal-goals'].summary?.deleted ?? 0 }}</div>
                </div>
              </div>
              <div class="mb-2 text-sm text-gray-500">
                {{ (data['personal-goals'].goals || []).length }} goal(s) ·
                {{ (data['personal-goals'].auditLogs || []).length }} audit row(s)
              </div>
              <DataTable
                v-if="(data['personal-goals'].goals || []).length > 0"
                :value="data['personal-goals'].goals"
                dataKey="_id"
                v-model:expandedRows="expandedPersonalGoalRows"
                class="p-datatable-sm"
                sortField="creationDate"
                :sortOrder="-1"
                :paginator="true"
                :rows="15"
                :rowsPerPageOptions="[10, 15, 30]"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
              >
                <Column expander style="width: 3rem" />
                <Column field="goalType" header="Type" :sortable="true" style="min-width: 120px" />
                <Column field="goalDate" header="Target date" :sortable="true" style="min-width: 120px" />
                <Column field="status" header="Status" :sortable="true" style="width: 120px">
                  <template #body="{ data: row }">
                    <Tag :value="row.status || '—'" :severity="personalGoalStatusSeverity(row.status)" />
                  </template>
                </Column>
                <Column field="creationDate" header="Created at" :sortable="true" style="min-width: 150px">
                  <template #body="{ data: row }">
                    {{ formatDate(row.creationDate) }}
                  </template>
                </Column>
                <Column
                  field="completedOrDeletedAt"
                  header="Completed / Deleted"
                  :sortable="true"
                  style="min-width: 170px"
                >
                  <template #body="{ data: row }">
                    <span v-if="row.completionDate">{{ formatDate(row.completionDate) }}</span>
                    <span v-else-if="row.deletionDate">{{ formatDate(row.deletionDate) }}</span>
                    <span v-else class="text-gray-400">—</span>
                  </template>
                </Column>
                <template #expansion="{ data: row }">
                  <div class="p-3 bg-gray-50 border-round">
                    <div class="text-sm font-semibold mb-2">Audit trail (this goal)</div>
                    <DataTable
                      v-if="auditLogsForGoal(row._id).length"
                      :value="auditLogsForGoal(row._id)"
                      class="p-datatable-sm"
                      :paginator="auditLogsForGoal(row._id).length > 8"
                      :rows="8"
                    >
                      <Column field="eventType" header="Event" style="min-width: 140px">
                        <template #body="{ data: log }">
                          <Tag :value="formatAuditEventLabel(log.eventType)" :severity="auditEventSeverity(log.eventType)" />
                        </template>
                      </Column>
                      <Column field="timestamp" header="Timestamp" style="min-width: 180px">
                        <template #body="{ data: log }">
                          {{ formatTimestamp(log.timestamp) }}
                        </template>
                      </Column>
                      <Column header="Details" style="min-width: 220px">
                        <template #body="{ data: log }">
                          <span v-if="log.changes && Object.keys(log.changes).length" class="text-xs font-mono break-words">
                            {{ formatAuditChanges(log.changes) }}
                          </span>
                          <span v-else-if="log.goalSnapshot" class="text-xs text-gray-600">
                            {{ snapshotSummary(log.goalSnapshot) }}
                          </span>
                          <span v-else class="text-gray-400">—</span>
                        </template>
                      </Column>
                    </DataTable>
                    <div v-else class="text-sm text-gray-500">No audit entries for this goal (older events may predate logging).</div>
                  </div>
                </template>
              </DataTable>
              <div v-else class="p-6 text-center text-gray-400">No personal goals for this user.</div>
            </div>
            <div v-else class="p-6 text-center text-gray-400">No personal goals data available.</div>
          </TabPanel>

          <!-- AUDIT TAB -->
          <TabPanel value="audit">
            <div v-if="loading.audit" class="flex justify-center p-6"><ProgressSpinner /></div>
            <div v-else-if="data.audit" class="p-4">
              <div class="mb-3 text-sm text-gray-500">{{ data.audit.total }} audit events (last 50 per source: Travel Buddy, Level, Assessment, Profile, Dev Tips, Teams)</div>
              <DataTable :value="data.audit.logs" :paginator="true" :rows="20"
                :rowsPerPageOptions="[10, 20, 50]"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}">
                <Column field="createdAt" header="Timestamp" :sortable="true" style="min-width: 180px">
                  <template #body="{ data: row }">
                    {{ formatTimestamp(row.createdAt || row.timestamp) }}
                  </template>
                </Column>
                <Column field="auditSource" header="Source" :sortable="true" style="width: 140px">
                  <template #body="{ data: row }">
                    <Tag
                      :value="auditSourceLabel(resolveAuditSource(row))"
                      :severity="auditSourceSeverity(resolveAuditSource(row))"
                    />
                  </template>
                </Column>
                <Column field="eventType" header="Event Type" :sortable="true" style="min-width: 160px">
                  <template #body="{ data: row }">
                    {{ formatAuditEventLabel(row.eventType || row.activityType) }}
                  </template>
                </Column>
                <Column header="Details" style="min-width: 200px">
                  <template #body="{ data: row }">
                    <span v-if="row.reason">{{ row.reason }}</span>
                    <span v-else-if="row.details">{{ typeof row.details === 'string' ? row.details : JSON.stringify(row.details) }}</span>
                    <span v-else-if="row.metadata">{{ typeof row.metadata === 'string' ? row.metadata : JSON.stringify(row.metadata) }}</span>
                    <span v-else class="text-gray-400">—</span>
                  </template>
                </Column>
              </DataTable>
            </div>
            <div v-else class="p-6 text-center text-gray-400">No audit data available.</div>
          </TabPanel>

        </TabPanels>
      </Tabs>
    </div>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import ProgressSpinner from 'primevue/progressspinner';
import ProgressBar from 'primevue/progressbar';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import Button from 'primevue/button';
import usersService from '@/services/usersService';
import NetworkContent from '@/helpers/networking/NetworkContent';
import {
  formatKssRecurrenceLabel,
  formatKssSeriesEnd,
  formatKssUpcomingPreview,
  getKssPlannedScheduledCount,
} from '@/helpers/kssSeriesDisplay';
import CopyableField from '@/components/ContentManagement/components/CopyableField.vue';

export default {
  name: 'UserOverviewDialog',
  components: {
    Dialog,
    DataTable,
    Column,
    Tag,
    ProgressSpinner,
    ProgressBar,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    CopyableField,
    Button,
  },
  props: {
    visible: { type: Boolean, default: false },
    user: { type: Object, default: null },
  },
  emits: ['update:visible'],
  data() {
    return {
      activeTab: 'profile',
      loadedTabs: new Set(),
      expandedPersonalGoalRows: {},
      loading: {
        profile: false,
        devtips: false,
        'travel-buddy': false,
        assessments: false,
        levels: false,
        kss: false,
        'personal-goals': false,
        audit: false,
      },
      endingKssSeriesId: null,
      data: {
        profile: null,
        devtips: null,
        'travel-buddy': null,
        assessments: null,
        levels: null,
        kss: null,
        'personal-goals': null,
        audit: null,
      },
    };
  },
  computed: {
    assessmentCounts() {
      const list = this.data.assessments?.assessments || [];
      const completed = list.filter(a => a.status === 'completed' || a.status === 'done').length;
      const types = new Set(list.map(a => a.type)).size;
      return {
        completed,
        open: list.length - completed,
        types,
      };
    },
    levelSummary() {
      const levels = this.data.levels?.levels || [];
      return {
        totalXP: levels.reduce((sum, l) => sum + (l.currentXP || 0), 0),
        completed: levels.filter(l => l.completed).length,
        total: levels.length,
      };
    },
  },
  watch: {
    visible(val) {
      if (val && this.user) {
        this.resetAndLoad();
      } else if (!val) {
        this.activeTab = 'profile';
        this.loadedTabs = new Set();
        this.expandedPersonalGoalRows = {};
        this.data = {
          profile: null,
          devtips: null,
          'travel-buddy': null,
          assessments: null,
          levels: null,
          kss: null,
          'personal-goals': null,
          audit: null,
        };
      }
    },
  },
  methods: {
    resetAndLoad() {
      this.activeTab = 'profile';
      this.loadedTabs = new Set();
      Object.keys(this.data).forEach(k => { this.data[k] = null; });
      this.fetchTabData('profile');
    },

    onTabChange(value) {
      this.activeTab = value;
      if (!this.loadedTabs.has(value)) {
        this.fetchTabData(value);
      }
    },

    async fetchTabData(section) {
      if (!this.user?._id) return;
      this.loading[section] = true;
      try {
        const result = await usersService.getUserOverview(this.user._id, section);
        this.data[section] = result;
        this.loadedTabs.add(section);
      } catch (err) {
        console.error(`Failed to load ${section}:`, err);
        this.data[section] = null;
      } finally {
        this.loading[section] = false;
      }
    },

    kssRhythmLabel(row) {
      return formatKssRecurrenceLabel(row, (key, params) => this.$t(key, params));
    },

    kssSeriesEndLabel(row) {
      return formatKssSeriesEnd(row, (key) => this.$t(key));
    },

    kssPlannedCount(row) {
      return getKssPlannedScheduledCount(row);
    },

    kssUpcomingDates(row) {
      return formatKssUpcomingPreview(row, 6);
    },

    canAdminEndKssSeries(row) {
      return row?.status === 'active'
        && row?.isRecurring === true
        && row?.seriesEnded !== true;
    },

    confirmEndKssSeries(row) {
      const seriesId = row.seriesId || row._id;
      const name = this.getLocalizedText(row.name) || 'this series';
      if (!seriesId) return;
      this.$confirm.require({
        message: `End "${name}"? Future scheduled meetings will be removed. Submitted and in-progress trackers will be kept.`,
        header: 'End KSS series',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.endKssSeriesAdmin(seriesId),
      });
    },

    async endKssSeriesAdmin(seriesId) {
      this.endingKssSeriesId = seriesId;
      try {
        await NetworkContent.endKssSeries(seriesId);
        await this.fetchTabData('kss');
        this.$toast?.add?.({
          severity: 'success',
          summary: 'KSS series ended',
          detail: 'Scheduled occurrences removed; history preserved.',
          life: 4000,
        });
      } catch (err) {
        console.error('Failed to end KSS series:', err);
        this.$toast?.add?.({
          severity: 'error',
          summary: err.response?.data?.message || 'Failed to end KSS series',
          life: 4000,
        });
      } finally {
        this.endingKssSeriesId = null;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    formatTimestamp(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleString('en-GB', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        fractionalSecondDigits: 3,
      });
    },

    getLocalizedText(val) {
      if (!val) return '';
      if (typeof val === 'string') return val;
      return val.en || val.de || Object.values(val)[0] || '';
    },

    meetingStatusSeverity(status) {
      const map = { completed: 'success', active: 'info', open: 'info', cancelled: 'danger', skipped: 'warn' };
      return map[status] || 'secondary';
    },

    assessmentStatusSeverity(status) {
      const map = {
        completed: 'success',
        done: 'success',
        open: 'info',
        pending: 'warn',
        active: 'info',
        ended: 'warn',
        archived: 'secondary',
      };
      return map[status] || 'secondary';
    },

    personalGoalStatusSeverity(status) {
      const map = { active: 'success', completed: 'info', deleted: 'secondary' };
      return map[status] || 'secondary';
    },

    auditLogsForGoal(goalId) {
      const pg = this.data['personal-goals'];
      const logs = pg?.auditLogs || [];
      const id = goalId != null ? String(goalId) : '';
      return logs.filter((l) => String(l.goalId) === id);
    },

    /** Prefer `auditSource` — not `_source` (underscore keys can be dropped) and not `source` (Level/TB channel). */
    resolveAuditSource(row) {
      if (!row || typeof row !== 'object') return undefined;
      return row.auditSource ?? row._source;
    },

    auditSourceLabel(src) {
      if (src === 'travel_buddy') return 'Travel Buddy';
      if (src === 'level_system') return 'Level System';
      if (src === 'assessment') return 'Assessment';
      if (src === 'profile') return 'Profile';
      if (src === 'dev_tip') return 'Dev Tips';
      if (src === 'team') return 'Teams';
      if (src === 'cms') return 'CMS';
      return src || '—';
    },

    auditSourceSeverity(src) {
      if (src === 'travel_buddy') return 'info';
      if (src === 'level_system') return 'warn';
      if (src === 'assessment') return 'success';
      if (src === 'profile') return 'warn';
      if (src === 'dev_tip') return 'info';
      if (src === 'team') return 'info';
      if (src === 'cms') return 'warn';
      return 'secondary';
    },

    formatAuditEventLabel(eventType) {
      const labels = {
        goal_created: 'Created',
        goal_updated: 'Updated',
        goal_completed: 'Completed',
        goal_deleted: 'Deleted',
        devtip_assigned: 'Tip assigned',
        devtip_feedback_submitted: 'Tip feedback',
        devtip_marked_done: 'Tip marked done',
        devtip_assignment_skipped: 'Tip not assigned',
        feedback_submitted: 'Feedback',
        assessment_started: 'Assessment started',
        assessment_progress_saved: 'Assessment progress saved',
        assessment_submitted: 'Assessment submitted',
        assessment_expired: 'Assessment expired',
        assessment_results_viewed: 'Assessment results viewed',
        email_changed: 'Email changed',
        password_reset_requested: 'Password reset requested',
        password_reset_completed: 'Password reset completed',
        role_changed: 'Roles changed',
        user_deactivated: 'User deactivated',
        user_reactivated: 'User reactivated',
        team_created: 'Team created',
        team_updated: 'Team updated',
        team_deleted: 'Team deleted',
        team_member_added: 'Team member added',
        team_member_removed: 'Team member removed',
        team_role_changed: 'Team role changed',
        level_target_created: 'Level template created',
        level_target_deleted: 'Level template deleted',
        level_template_user_sync: 'Level template → user progress',
        level_template_goal_removed: 'Level template goal removed',
        xp_award: 'XP awarded',
        level_change: 'Level changed',
        admin_repair: 'Level system repair',
      };
      return labels[eventType] || eventType || '—';
    },

    auditEventSeverity(eventType) {
      const map = {
        goal_created: 'success',
        goal_updated: 'info',
        goal_completed: 'success',
        goal_deleted: 'secondary',
        devtip_assigned: 'info',
        devtip_feedback_submitted: 'info',
        devtip_marked_done: 'success',
        devtip_assignment_skipped: 'warn',
        feedback_submitted: 'warn',
      };
      return map[eventType] || 'secondary';
    },

    formatAuditChanges(changes) {
      try {
        return JSON.stringify(changes);
      } catch {
        return String(changes);
      }
    },

    snapshotSummary(snap) {
      if (!snap || typeof snap !== 'object') return '—';
      const parts = [];
      if (snap.goalName) parts.push(String(snap.goalName));
      if (snap.status) parts.push(`status: ${snap.status}`);
      return parts.join(' · ') || '—';
    },
  },
};
</script>

<style scoped>
.user-overview-dialog :deep(.p-dialog-content) {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.user-overview-dialog :deep(.p-tabpanels) {
  padding: 0;
}
</style>
